import React, { useEffect, useMemo, useState } from 'react';
import { marketApi } from './api';
import PriceChart from './PriceChart';

const RANGES = ['1d', '5d', '1m', '3m', '6m', 'ytd', '1y', '5y', 'all'];
const INTERVALS = ['1m', '5m', '15m', '30m', '1h', '4h', '1D', '1W', '1M'];

export default function TickerChartCard({ symbol, tall = false, defaultRange = '3m', defaultInterval = '1D' }) {
  const [range, setRange] = useState(defaultRange);
  const [interval, setInterval] = useState(defaultInterval);
  const [chartType, setChartType] = useState('candles');
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    setError(null);
    marketApi
      .history(symbol, range, interval)
      .then((payload) => {
        if (cancelled) return;
        const candles = Array.isArray(payload) ? payload : payload?.candles || [];
        setData(candles);
        setMeta(payload?.meta || null);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [symbol, range, interval]);

  const rangeLabel = useMemo(() => range.toUpperCase(), [range]);

  if (!symbol) return null;

  return (
    <div className="chart-card">
      <div className="chart-card-header">
        <div>
          <h3>{symbol} price history</h3>
          <p className="muted compact">Provider: {meta?.provider || 'loading'} · Interval: {meta?.interval || interval}</p>
        </div>
        <div className="chart-controls">
          <div className="chart-range-buttons">
            {RANGES.map((r) => (
              <button key={r} className={r === range ? 'active' : ''} onClick={() => setRange(r)}>
                {r.toUpperCase()}
              </button>
            ))}
          </div>
          <select value={interval} onChange={(event) => setInterval(event.target.value)}>
            {INTERVALS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select value={chartType} onChange={(event) => setChartType(event.target.value)}>
            <option value="candles">Candles</option>
            <option value="line">Line</option>
            <option value="area">Area</option>
          </select>
        </div>
      </div>
      {error ? (
        <div className="error-box">Chart unavailable: {error}</div>
      ) : loading ? (
        <div className="skeleton-list">
          <span className="skeleton-bar" style={{ width: '100%', height: tall ? 320 : 240 }} />
        </div>
      ) : !data || data.length === 0 ? (
        <div className="empty-state">
          <p>No price data available for {rangeLabel}.</p>
          <small>Try a different range or interval.</small>
        </div>
      ) : (
        <>
          <PriceChart data={data} tall={tall} chartType={chartType} showVolume />
          <div className="chart-footer-meta">
            <span>Source: {meta?.source || 'provider'}</span>
            <span>{meta?.marketClosed ? 'Market closed' : 'Live data window'}</span>
          </div>
        </>
      )}
    </div>
  );
}
